import React, { useState } from 'react';
import { MessageCircle, Send, ShieldCheck, Briefcase, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitBulkInquiry } from '@/lib/api';
import { useApp } from '@/state/AppContext';
import { Footer } from '@/components/Footer';
import { toast } from 'sonner';

const BulkOrdersPage = () => {
  const { config } = useApp();
  const [form, setForm] = useState({
    company: '', contact_name: '', email: '', phone: '',
    occasion: '', quantity: 50, budget: '', product_interest: 'Choco Snaps',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.company || !form.contact_name || !form.email || !form.phone || !form.quantity) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity) };
      await submitBulkInquiry(payload);
      setSuccess(true);
      toast.success('Inquiry sent!', { description: 'We’ll reach out within 24h.' });
    } catch (e) {
      toast.error('Could not submit', { description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const waMsg = encodeURIComponent('Hi Big Bash, I’d like a bulk-order quote.');
  const waHref = `https://wa.me/${config.whatsapp_number}?text=${waMsg}`;

  return (
    <div className="bg-white" data-testid="bulk-orders-page">
      <section className="relative overflow-hidden bb-block-dark">
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs font-nunito-black uppercase tracking-wider">
            <Briefcase className="w-4 h-4" /> For Corporates
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95] text-white">Bulk &amp; Corporate Orders</h1>
          <p className="text-sm md:text-base mt-3 max-w-xl text-white/85 font-nunito-medium">Branded chocolates for client gifting, employee celebrations, product launches and weddings. Minimum 50 pieces. White-glove account management included.</p>
          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            {['Custom logo printing', 'Premium packaging', 'Pan-India delivery', 'Dedicated account manager'].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur font-nunito-bold"><Sparkles className="w-3 h-3" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-6 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          {success ? (
            <div className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)] p-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-[var(--bb-yellow)] flex items-center justify-center border-2 border-[var(--bb-darkblue)]"><Send className="w-7 h-7 text-[var(--bb-darkblue)]" /></div>
              <h2 className="font-display text-3xl mt-3 text-[var(--bb-darkblue)]">Inquiry sent!</h2>
              <p className="text-sm text-[var(--bb-darkgrey)] mt-1 font-nunito-medium">Our team will reach out within 24 hours. For urgent orders, WhatsApp us.</p>
              <a href={waHref} target="_blank" rel="noreferrer">
                <Button className="mt-4 h-12 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white font-nunito-black ">
                  <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp +91 93214 50920
                </Button>
              </a>
            </div>
          ) : (
            <div className="rounded-2xl bg-white border-2 border-[var(--bb-darkblue)] p-5" data-testid="bulk-orders-form">
              <h2 className="font-display text-3xl text-[var(--bb-darkblue)]">Request a quote</h2>
              <p className="text-xs text-[var(--bb-darkgrey)] mt-1 font-nunito-medium">Tell us a bit and we'll send pricing + samples.</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div>
                  <Label className="text-xs font-nunito-black">Company *</Label>
                  <Input value={form.company} onChange={update('company')} className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-company" />
                </div>
                <div>
                  <Label className="text-xs font-nunito-black">Contact name *</Label>
                  <Input value={form.contact_name} onChange={update('contact_name')} className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-name" />
                </div>
                <div>
                  <Label className="text-xs font-nunito-black">Email *</Label>
                  <Input type="email" value={form.email} onChange={update('email')} className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-email" />
                </div>
                <div>
                  <Label className="text-xs font-nunito-black">Phone *</Label>
                  <Input value={form.phone} onChange={update('phone')} className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-phone" />
                </div>
                <div>
                  <Label className="text-xs font-nunito-black">Occasion</Label>
                  <Input value={form.occasion} onChange={update('occasion')} placeholder="Diwali, Annual Day, Wedding…" className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-occasion" />
                </div>
                <div>
                  <Label className="text-xs font-nunito-black">Approx. quantity *</Label>
                  <Input type="number" min={50} value={form.quantity} onChange={update('quantity')} className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-quantity" />
                </div>
                <div>
                  <Label className="text-xs font-nunito-black">Budget (₹)</Label>
                  <Input value={form.budget} onChange={update('budget')} placeholder="e.g. 50,000" className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-budget" />
                </div>
                <div>
                  <Label className="text-xs font-nunito-black">Product interest</Label>
                  <Select value={form.product_interest} onValueChange={update('product_interest')}>
                    <SelectTrigger className="h-11 rounded-xl mt-1 border-2" data-testid="bulk-interest"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Choco Snaps">Choco Snaps (tiles)</SelectItem>
                      <SelectItem value="ChocoDigits">Choco Digits</SelectItem>
                      <SelectItem value="3D Choco Pops">3D Choco Pops (animals)</SelectItem>
                      <SelectItem value="Mix">A mix</SelectItem>
                      <SelectItem value="Not sure">Not sure - suggest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-nunito-black">Message / requirements</Label>
                  <Textarea value={form.message} onChange={update('message')} rows={4} placeholder="Logo, theme, deadline, delivery cities…" className="rounded-xl mt-1 border-2" data-testid="bulk-message" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button onClick={submit} disabled={submitting} className="h-12 px-5 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black " data-testid="bulk-submit">
                  <Send className="w-4 h-4 mr-1.5" /> {submitting ? 'Sending…' : 'Send inquiry'}
                </Button>
                <a href={waHref} target="_blank" rel="noreferrer">
                  <Button type="button" className="h-12 px-5 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white font-nunito-black " data-testid="bulk-orders-whatsapp-button">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp +91 93214 50920
                  </Button>
                </a>
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[var(--bb-darkgrey)] font-nunito-medium">
                <ShieldCheck className="w-4 h-4 text-[var(--bb-pink)]" /> Your details are kept confidential and used only to respond to your inquiry.
              </div>
            </div>
          )}
        </div>

        <aside className="space-y-3">
          {[
            { title: 'Pricing tiers', body: '50-99 pcs · 100-499 pcs · 500-1999 pcs · 2000+ pcs. Volume discounts kick in at 100+.', color: 'pink' },
            { title: 'Lead time', body: 'Standard 7-10 days. Rush 3-5 days subject to availability.', color: 'cyan' },
            { title: 'Branding', body: 'Edible print on every piece. Custom outer packaging available.', color: 'yellow' },
            { title: 'Industries we love', body: 'Tech, fintech, banks, schools, weddings, event agencies, real estate.', color: 'dark' },
          ].map((c) => (
            <div key={c.title} className={`rounded-2xl border-2 border-[var(--bb-darkblue)] p-4 ${c.color === 'pink' ? 'bb-block-pink text-white' : c.color === 'cyan' ? 'bb-block-cyan text-white' : c.color === 'yellow' ? 'bb-block-yellow' : 'bb-block-dark text-white'}`}>
              <div className="text-sm font-nunito-black">{c.title}</div>
              <div className="text-xs mt-1 font-nunito-medium opacity-95">{c.body}</div>
            </div>
          ))}
        </aside>
      </section>

      <Footer />
    </div>
  );
};

export default BulkOrdersPage;
